import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ConsentPreferences,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  defaultConsent,
} from "../config/cookieConfig";

declare global {
  function gtag(...args: unknown[]): void;
  function rdt(...args: unknown[]): void;
  function fbq(...args: unknown[]): void;
}

/**
 * Update Google Consent Mode state
 */
function updateGoogleConsent(analytics: boolean, marketing: boolean) {
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
  }
}

// Track if pixels have been initialized to avoid double-init
let pixelsInitialized = false;

/**
 * Initialize marketing pixels (Reddit + Meta) - only called when marketing consent granted
 */
function initMarketingPixels() {
  if (pixelsInitialized || import.meta.env.DEV) return;
  pixelsInitialized = true;

  // Reddit Pixel
  const redditPixelId = import.meta.env.VITE_REDDIT_PIXEL_ID;
  if (redditPixelId && !window.rdt) {
    interface RdtFunc {
      (...args: unknown[]): void;
      sendEvent?: (...args: unknown[]) => void;
      callQueue: unknown[][];
    }
    const rdtFunc: RdtFunc = function (...args: unknown[]) {
      if (rdtFunc.sendEvent) {
        rdtFunc.sendEvent(...args);
      } else {
        rdtFunc.callQueue.push(args);
      }
    };
    rdtFunc.callQueue = [];
    window.rdt = rdtFunc;
    const script = document.createElement("script");
    script.src = "https://www.redditstatic.com/ads/pixel.js";
    script.async = true;
    document.head.appendChild(script);
    rdt("init", redditPixelId);
    rdt("track", "PageVisit");
  }

  // Meta Pixel
  const metaPixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (metaPixelId && !window.fbq) {
    interface FbqFunc {
      (...args: unknown[]): void;
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      loaded: boolean;
      version: string;
    }
    const fbqFunc: FbqFunc = function (...args: unknown[]) {
      if (fbqFunc.callMethod) {
        fbqFunc.callMethod(...args);
      } else {
        fbqFunc.queue.push(args);
      }
    };
    fbqFunc.queue = [];
    fbqFunc.loaded = true;
    fbqFunc.version = "2.0";
    window.fbq = fbqFunc;
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.async = true;
    document.head.appendChild(script);
    fbq("init", metaPixelId);
    fbq("track", "PageView");
  }
}

interface ConsentContextType {
  consent: ConsentPreferences | null;
  hasConsented: boolean;
  showBanner: boolean;
  showPreferencesModal: boolean;
  updateConsent: (prefs: Partial<ConsentPreferences>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  savePreferences: (analytics: boolean, marketing: boolean) => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

// Global consent state for non-React code (e.g., analyticsUtils.ts)
let globalConsent: ConsentPreferences | null = null;

/**
 * Get current consent preferences for non-React code
 */
export function getConsent(): ConsentPreferences | null {
  return globalConsent;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return ctx;
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentPreferences | null>(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentPreferences;
        // Check if consent version matches (re-consent if policy updated)
        if (parsed.consentVersion === CONSENT_VERSION) {
          setConsent(parsed);
          globalConsent = parsed;
          setHasConsented(true);
          setShowBanner(false);
          // Restore Google Consent Mode state
          updateGoogleConsent(parsed.analytics, parsed.marketing);
          // Initialize marketing pixels if previously consented
          if (parsed.marketing) {
            initMarketingPixels();
          }
        } else {
          // Policy version changed, need re-consent
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    } else {
      // No consent stored, show banner
      setShowBanner(true);
    }
    setInitialized(true);
  }, []);

  const saveToStorage = useCallback((prefs: ConsentPreferences) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
    setConsent(prefs);
    globalConsent = prefs;
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferencesModal(false);
    // Sync with Google Consent Mode
    updateGoogleConsent(prefs.analytics, prefs.marketing);
    // Initialize marketing pixels if consented
    if (prefs.marketing) {
      initMarketingPixels();
    }
  }, []);

  const updateConsent = useCallback(
    (prefs: Partial<ConsentPreferences>) => {
      const updated: ConsentPreferences = {
        ...defaultConsent,
        ...consent,
        ...prefs,
        necessary: true, // Always true
        consentTimestamp: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(updated);
    },
    [consent, saveToStorage]
  );

  const acceptAll = useCallback(() => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      consentTimestamp: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveToStorage(allAccepted);
  }, [saveToStorage]);

  const rejectAll = useCallback(() => {
    const allRejected: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      consentTimestamp: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveToStorage(allRejected);
  }, [saveToStorage]);

  const openPreferences = useCallback(() => {
    setShowPreferencesModal(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferencesModal(false);
  }, []);

  const savePreferences = useCallback(
    (analytics: boolean, marketing: boolean) => {
      const updated: ConsentPreferences = {
        necessary: true,
        analytics,
        marketing,
        consentTimestamp: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(updated);
    },
    [saveToStorage]
  );

  // Don't render children until initialized to prevent flash
  if (!initialized) {
    return null;
  }

  return (
    <ConsentContext.Provider
      value={{
        consent,
        hasConsented,
        showBanner,
        showPreferencesModal,
        updateConsent,
        acceptAll,
        rejectAll,
        openPreferences,
        closePreferences,
        savePreferences,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}
